import React, { Component } from 'react';
import { List } from 'immutable';
import log from 'loglevel';
import PropTypes from 'prop-types';
// import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';

import { MESSAGES_TYPES } from 'constants';
import { Video, Image, Message, Carousel, Buttons } from 'messagesComponents';

import './styles.scss';
import ThemeContext from '../../../../ThemeContext';
import { VIEWPORT_RESIZE_EVENT } from '../../../../../../utils/use-visual-viewport';

const isToday = (date) => {
    const today = new Date();
    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
};

const formatDate = (date) => {
    const dateToFormat = new Date(date);
    const showDate = isToday(dateToFormat) ? '' : `${dateToFormat.toLocaleDateString()} `;
    return `${showDate}${dateToFormat.toLocaleTimeString('en-US', { timeStyle: 'short' })}`;
};

const scrollToBottom = () => {
    const messagesDiv = document.getElementById('rw-messages');
    if (messagesDiv) {
        messagesDiv.scrollTop = messagesDiv.scrollHeight;
    }
};

class Messages extends Component {
    currentMessages = List();
    prevTypingIndication = undefined;
    prevMessages = undefined;
    scrollTimeout = undefined;
    scrollToElement = undefined;

    componentDidMount() {
        scrollToBottom();
        // The mobile takeover resizes the list when the keyboard opens/closes;
        // keep the newest content visible instead of leaving it under the keyboard.
        window.addEventListener(VIEWPORT_RESIZE_EVENT, scrollToBottom);
    }

    componentWillUnmount() {
        window.removeEventListener(VIEWPORT_RESIZE_EVENT, scrollToBottom);
        clearTimeout(this.scrollTimeout);
    }

    componentDidUpdate() {
        if (this.prevMessages !== undefined) {
            this.scrollToContent(
                this.prevMessages,
                this.currentMessages,
                this.prevTypingIndication,
                this.props.displayTypingIndication
            );
        }

        this.prevTypingIndication = this.props.displayTypingIndication;
        this.prevMessages = this.currentMessages;
    }

    scrollTo(element) {
        if (element) {
            log.debug('scrolling to element', element);
            const messagesDiv = document.getElementById('rw-messages');
            if (messagesDiv) {
                // Use scrollTop instead of scrollIntoView to avoid scrolling
                // ancestor containers (which breaks embedded widget layout)
                const containerRect = messagesDiv.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const scrollOffset = elementRect.top - containerRect.top + messagesDiv.scrollTop;
                messagesDiv.scrollTo({ top: scrollOffset, behavior: 'smooth' });
            } else {
                scrollToBottom();
            }
        }
    }

    scheduleScroll(element, delayMs) {
        // keep element to scroll to unchanged, until we've actually done the scroll (from
        // within setTimeout below):
        if (!this.scrollToElement) {
            this.scrollToElement = element;
        }

        // scroll after timeout, as more content elements might get rendered right after this one,
        // and we don't want to scroll to each one of them within a matter of milliseconds, but just
        // to the top one once:
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
            this.scrollTo(this.scrollToElement);

            this.scrollToElement = undefined;
            this.scrollTimeout = undefined;
        }, delayMs);
    }

    scrollToTopOfSenderContent(prevMessages, currentMessages, sender) {
        const elements = document.getElementsByClassName(`rw-sender-${sender}`);
        const scrollIdx = currentMessages.size - (currentMessages.size - prevMessages.size);

        if (elements.length > scrollIdx) {
            this.scheduleScroll(elements[scrollIdx], 250);
        }
    }

    scrollToContentByMessages(
        prevMessages,
        currentMessages,
        prevTypingIndication,
        currentTypingIndication
    ) {
        const prevVisibleMsgs = prevMessages.filter((m) => !m.get('hidden'));
        const curVisibleMsgs = currentMessages.filter((m) => !m.get('hidden'));

        const prevResponseMsgs = prevVisibleMsgs.filter((m) => m.get('sender') === 'response');
        const curResponseMsgs = curVisibleMsgs.filter((m) => m.get('sender') === 'response');

        if (curResponseMsgs.size > prevResponseMsgs.size) {
            // for new response messages, scroll to top of new content:
            this.scrollToTopOfSenderContent(prevResponseMsgs, curResponseMsgs, 'response');
        } else if (!prevTypingIndication && currentTypingIndication) {
            // or if typing indicator is to be shown:
            scrollToBottom();
        } else if (curVisibleMsgs.size > prevVisibleMsgs.size) {
            // else default behavior:
            scrollToBottom();
        }
    }

    scrollToContent(prevMessages, currentMessages, prevTypingIndication, currentTypingIndication) {
        if (prevMessages && currentMessages) {
            this.scrollToContentByMessages(
                prevMessages,
                currentMessages,
                prevTypingIndication,
                currentTypingIndication
            );
        } else {
            scrollToBottom();
        }
    }

    getComponentToRender = (message, index, isLast) => {
        const { params } = this.props;
        const ComponentToRender = (() => {
            switch (message.get('type')) {
                case MESSAGES_TYPES.TEXT: {
                    return Message;
                }
                case MESSAGES_TYPES.CAROUSEL: {
                    return Carousel;
                }
                case MESSAGES_TYPES.VIDREPLY.VIDEO: {
                    return Video;
                }
                case MESSAGES_TYPES.IMGREPLY.IMAGE: {
                    return Image;
                }
                case MESSAGES_TYPES.BUTTONS: {
                    return Buttons;
                }
                case MESSAGES_TYPES.CUSTOM_COMPONENT:
                    return connect(
                        (store) => ({ store }),
                        (dispatch) => ({ dispatch })
                    )(this.props.customComponent);
                default:
                    return null;
            }
        })();
        if (message.get('type') === 'component') {
            const messageProps = message.get('props');
            return (
                <ComponentToRender
                    id={index}
                    {...(messageProps.toJS ? messageProps.toJS() : messageProps)}
                    isLast={isLast}
                />
            );
        }
        return (
            <ComponentToRender
                id={index}
                messageIndex={index}
                params={params}
                message={message}
                isLast={isLast}
            />
        );
    };

    render() {
        const { displayTypingIndication } = this.props;

        const renderMessages = () => {
            const { messages, showMessageDate } = this.props;

            this.currentMessages = messages;

            if (messages.isEmpty()) return null;

            const groups = [];
            let group = null;

            const dateRenderer =
                typeof showMessageDate === 'function'
                    ? showMessageDate
                    : showMessageDate === true
                      ? formatDate
                      : null;

            const renderMessageDate = (message) => {
                const timestamp = message.get('timestamp');

                if (!dateRenderer || !timestamp) return null;
                const dateToRender = dateRenderer(message.get('timestamp', message));
                return dateToRender ? (
                    <span className="rw-message-date">
                        {dateRenderer(message.get('timestamp'), message)}
                    </span>
                ) : null;
            };

            const renderMessage = (message, index) => (
                <div className={`rw-message rw-sender-${message.get('sender')}`} key={index}>
                    {this.getComponentToRender(message, index, index === messages.size - 1)}
                    {renderMessageDate(message)}
                </div>
            );

            messages.forEach((msg, index) => {
                if (msg.get('hidden')) return;
                if (group === null || group.from !== msg.get('sender')) {
                    if (group !== null) groups.push(group);

                    group = {
                        from: msg.get('sender'),
                        messages: [],
                    };
                }

                group.messages.push(renderMessage(msg, index));
            });

            groups.push(group); // finally push last group of messages.

            return groups.map((g, index) => (
                <div className={`rw-group-message rw-from-${g && g.from}`} key={`group_${index}`}>
                    {g.messages}
                </div>
            ));
        };
        const { conversationBackgroundColor, assistBackgroundColor } = this.context;

        return (
            <div
                id="rw-messages"
                style={{ backgroundColor: conversationBackgroundColor }}
                className="rw-messages-container"
                role="log"
            >
                {renderMessages()}
                {displayTypingIndication && (
                    <div className="rw-message rw-typing-indication">
                        <div
                            style={{ backgroundColor: assistBackgroundColor }}
                            className="rw-response"
                        >
                            <div id="wave">
                                <span className="rw-dot" />
                                <span className="rw-dot" />
                                <span className="rw-dot" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
Messages.contextType = ThemeContext;
Messages.propTypes = {
    messages: PropTypes.any,
    customComponent: PropTypes.func,
    showMessageDate: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    displayTypingIndication: PropTypes.bool,
};

Message.defaultTypes = {
    displayTypingIndication: false,
};

export default connect((store) => ({
    messages: store.messages,
    displayTypingIndication: store.behavior.get('messageDelayed'),
}))(Messages);
